import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContexto = createContext(null);

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [suscripcion, setSuscripcion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [featuresPlan, setFeaturesPlan] = useState(null); // null = cargando; [] = plan sin features (fail-open en menu)
  const [tiendaVisible, setTiendaVisible] = useState(null); // null = cargando; true/false = valor confirmado

  const cargarFeatures = useCallback(async () => {
    try {
      const r = await api.get('/mi-plan');
      setFeaturesPlan(r.data.features_habilitadas || []);
    } catch {
      setFeaturesPlan([]); // fail-open
    }
    // Config global — tienda de complementos visible?
    try {
      const c = await api.get('/config-global/publico');
      setTiendaVisible(!!c.data.tienda_complementos_visible);
    } catch { /* silencio — default false */ }
  }, []);

  const verificarToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCargando(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      const { suscripcion: sub, tipo_negocio: tn, moneda: mon, ...usr } = res.data;
      setUsuario({ ...usr, tipo_negocio: tn || 'taller', moneda: mon });
      setSuscripcion(sub || null);
      if (mon) localStorage.setItem('tenant_moneda', JSON.stringify(mon));
      if (usr.rol !== 'super_admin') cargarFeatures();
    } catch {
      localStorage.removeItem('token');
      setUsuario(null);
      setSuscripcion(null);
    } finally {
      setCargando(false);
    }
  }, [cargarFeatures]);

  useEffect(() => {
    verificarToken();
  }, [verificarToken]);

  const iniciarSesion = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, usuario: usr, suscripcion: sub, email_no_verificado } = res.data;
    // If email not verified, throw with response data so Login.js can handle it
    if (email_no_verificado) {
      const err = new Error('Email no verificado');
      err.response = { data: res.data };
      throw err;
    }
    localStorage.setItem('token', token);
    setUsuario({ ...usr, tipo_negocio: usr.tipo_negocio || 'taller' });
    setSuscripcion(sub || null);
    if (usr.rol !== 'super_admin') cargarFeatures();
    // Cargar moneda del tenant tras login exitoso
    try {
      const meRes = await api.get('/auth/me');
      if (meRes.data?.moneda) {
        localStorage.setItem('tenant_moneda', JSON.stringify(meRes.data.moneda));
        setUsuario(prev => ({ ...prev, moneda: meRes.data.moneda }));
      }
    } catch { /* ignore */ }
    return usr;
  };

  const cerrarSesion = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenant_moneda');
    setUsuario(null);
    setSuscripcion(null);
    setFeaturesPlan(null);
    setTiendaVisible(null);
  };

  const refrescarUsuario = useCallback(async (nuevoToken = null) => {
    if (nuevoToken) localStorage.setItem('token', nuevoToken);
    try {
      const res = await api.get('/auth/me');
      const { suscripcion: sub, tipo_negocio: tn, moneda: mon, ...usr } = res.data;
      setUsuario(prev => ({ ...prev, ...usr, tipo_negocio: tn || 'taller', moneda: mon }));
      setSuscripcion(sub || null);
      if (mon) localStorage.setItem('tenant_moneda', JSON.stringify(mon));
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContexto.Provider value={{ usuario, suscripcion, cargando, featuresPlan, tiendaVisible, cargarFeatures, iniciarSesion, cerrarSesion, refrescarUsuario }}>
      {children}
    </AuthContexto.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContexto);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};
